import useCarers from '@features/organisation/hooks/useCarers'
import useResidentsList from 'hooks/useResidentsList'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceUserWords } from '../useServiceUserWords'
import { savedPersonName, singleSpaced } from './readRules'

type Named = { id: string; name?: { first?: string; last?: string } }

/** Looks a saved person's id up: the embedded name, the list's, or `notFound`. */
export type PersonName = (
  id: string | null | undefined,
  embeddedName?: unknown
) => string | null

/** `nameOf` reads nothing while `isLoading`; pass it on to `ReadItem`. */
export type PersonNames = {
  isError: boolean
  isLoading: boolean
  nameOf: PersonName
}

const useNameLookup = (
  people: ReadonlyArray<Named>,
  { isError, isLoading }: { isError: boolean; isLoading: boolean },
  notFound: string
): PersonNames => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const notLoaded = t(incidentReportsConstants.CASE_V2_VIEW_NOT_LOADED)
  const names = useMemo(
    () =>
      new Map(
        people.map((person) => [
          person.id,
          singleSpaced(`${person.name?.first ?? ''} ${person.name?.last ?? ''}`)
        ])
      ),
    [people]
  )

  const nameOf = useCallback<PersonName>(
    (id, embeddedName) => {
      if (isLoading || isError) {
        // Until the list has loaded, only a name the read embeds is known.
        const embedded = savedPersonName({
          embeddedName,
          id: null,
          names,
          notFound
        })
        return embedded ?? (isError && id ? notLoaded : null)
      }
      return savedPersonName({ embeddedName, id, names, notFound })
    },
    [isError, isLoading, names, notFound, notLoaded]
  )

  return { isError, isLoading, nameOf }
}

/**
 * Names a saved team member id, preferring a name the read already embeds.
 * The team list holds current team members only, so anyone since removed
 * reads "Team member not found", never the raw id.
 */
export const useTeamMemberName = (): PersonNames => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { carers, isError, isLoading } = useCarers()

  return useNameLookup(
    carers,
    { isError, isLoading },
    t(incidentReportsConstants.CASE_V2_VIEW_TEAM_MEMBER_NOT_FOUND)
  )
}

/**
 * Names a saved service user id the same way, from all the home's service
 * users; the list holds active ones only unless asked for any status.
 */
export const useServiceUserName = (): PersonNames => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { singular } = useServiceUserWords()
  const { residents, isError, isLoading } = useResidentsList({
    filters: { anyStatus: true }
  })

  return useNameLookup(
    residents,
    { isError, isLoading },
    t(incidentReportsConstants.CASE_V2_VIEW_SERVICE_USER_NOT_FOUND, {
      variable: singular
    })
  )
}
